import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Search, Landmark } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { PaymentStateBadge, SeatRequired, XeroSyncBadge } from '../../components/nursery/accounts/AccountBits';
import nurseryAccountsService, { type InvoiceFilters } from '../../services/nurseryAccounts';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import type { InvoiceRow } from '../../types/nurseryAccounts';

/** /accounts/invoices: every invoice, with what each still owes. */

type FilterKey = 'all' | 'outstanding' | 'overdue' | 'draft' | 'paid' | 'void' | 'xero_failed';

const FILTERS: { key: FilterKey; label: string; filters: InvoiceFilters }[] = [
  { key: 'all', label: 'All invoices', filters: {} },
  { key: 'outstanding', label: 'Awaiting payment', filters: { state: 'outstanding' } },
  { key: 'overdue', label: 'Overdue', filters: { state: 'overdue' } },
  { key: 'draft', label: 'Draft', filters: { status: 'draft' } },
  { key: 'paid', label: 'Paid', filters: { status: 'paid' } },
  { key: 'void', label: 'Void', filters: { status: 'cancelled' } },
  { key: 'xero_failed', label: 'Not in Xero', filters: { xero: 'failed' } },
];

function InvoiceList() {
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('accounts', ['user', 'admin']);

  const [filter, setFilter] = useState<FilterKey>('all');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search as they type, but not on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const chosen = FILTERS.find((f) => f.key === filter)?.filters ?? {};
      const res = await nurseryAccountsService.getInvoices({ ...chosen, ...(search ? { search } : {}) });
      if (res.success) setInvoices(res.data || []);
      else setError(errorMessage(res, 'Could not load the invoices'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the invoices'));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) return <SeatRequired seat="user" what="Seeing invoices" />;

  const date = (d: string | null) => (d ? formatLocalDateInTz(d.slice(0, 10), tz) : null);

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="Every invoice, who pays it and what it still owes."
        icon={<FileText className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/accounts/receivables')}>
              <Landmark className="h-4 w-4 mr-1" /> Receivables
            </Button>
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label htmlFor="invoice-search" className="block text-sm font-medium text-neutral-700 mb-1">Search</label>
            <div className="relative">
              <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input id="invoice-search" type="search" value={searchText} onChange={(e) => setSearchText(e.target.value)}
                placeholder="Invoice, customer, PO or store number"
                className="w-full border border-neutral-300 rounded-lg pl-9 pr-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label htmlFor="invoice-filter" className="block text-sm font-medium text-neutral-700 mb-1">Show</label>
            <select id="invoice-filter" value={filter} onChange={(e) => setFilter(e.target.value as FilterKey)}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              {FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        {loading ? (
          <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : invoices.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center text-sm text-neutral-500">
            {search || filter !== 'all'
              ? 'No invoices match. Try a different search, or show all invoices.'
              : 'No invoices yet. An order is invoiced for the trees that were picked, when its last tree is picked.'}
          </div>
        ) : (
          <ul className="space-y-3">
            {invoices.map((inv) => {
              const payer = inv.payer_name || inv.customer_name || 'No customer';
              const isSite = inv.payer_id != null && inv.customer_id != null && inv.payer_id !== inv.customer_id;
              return (
                <li key={inv.id}>
                  <Link to={`/accounts/invoices/${inv.id}`}
                    className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-semibold text-neutral-900 break-words min-w-0">{inv.invoice_number}</span>
                      <PaymentStateBadge state={inv.payment_state} />
                      <XeroSyncBadge status={inv.xero_sync_status} />
                      {inv.dispute_status === 'open' && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800">
                          Disputed
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-neutral-800 break-words">{payer}</p>
                    {isSite && (
                      <p className="text-sm text-neutral-600 break-words">
                        {inv.customer_name}{inv.customer_code ? ` #${inv.customer_code}` : ''}
                      </p>
                    )}
                    {!isSite && inv.customer_code && <p className="text-sm text-neutral-600">#{inv.customer_code}</p>}
                    <div className="mt-1 text-xs text-neutral-500 flex flex-wrap gap-x-3 gap-y-0.5">
                      {inv.customer_po && <span className="break-words min-w-0">PO {inv.customer_po}</span>}
                      <span>Invoiced {date(inv.invoice_date)}</span>
                      <span>{inv.due_date ? `Due ${date(inv.due_date)}` : 'No due date'}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                      <span className="text-neutral-600">Total <span className="font-medium text-neutral-900">{money(inv.total_amount)}</span></span>
                      <span className="text-neutral-600">Still owing <span className="font-semibold text-neutral-900">{money(inv.balance)}</span></span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

export default InvoiceList;
